// Snapshot Refresh Helpers
// -----------------------
// Pure utility functions for the DocumentsDashboard's real-time onSnapshot
// listener.  Extracted here so they can be unit-tested in isolation without
// rendering React components or mocking Firebase.
//
// Two responsibilities:
//
// 1. Fingerprint comparison — detect when business-level fields (generated,
//    approved, resetRequired) in 6_manageReports have changed, so the
//    Dashboard can trigger a context refresh.  Processing-status fields are
//    excluded because they are tracked in local component state.
//
// 2. Debounced processing-status display — when a processing status leaves
//    'processing', delay the UI update briefly (default 500ms) so that if
//    the auto-helper retry immediately sets it back to 'processing', the
//    user never sees the intermediate green flash.

/**
 * The set of manageReports fields that drive section row status on the
 * Documents Dashboard (generated, approved, reset flags).  Changes to any
 * of these should trigger a fetchPatientData to update the React context.
 *
 * Processing-status fields are deliberately excluded — they are tracked
 * in local component state via the onSnapshot and do not require a full
 * context refresh.
 */
export const FINGERPRINT_FIELDS = [
  'ciReportGenerated', 'ciReportApproved',
  'ssiReportGenerated', 'ssiReportApproved', 'ssiResetRequired',
  'ddReportGenerated', 'ddReportApproved',
  'padReportGenerated', 'padReportApproved',
  'qbCheckReportGenerated', 'qbCheckReportApproved',
  'qbTestReportGenerated', 'qbTestReportApproved',
  'cpReportGenerated', 'cpReportApproved',
  'prReportGenerated', 'prReportApproved',
  'bupaLetterReportGenerated', 'bupaLetterReportApproved',
];

/**
 * Map a raw Firestore processing status to the value shown on the dashboard.
 * Cloud Functions write ``complete`` when generation finishes; the dashboard
 * treats that as ``idle`` for row styling (amber only while ``processing``).
 */
const normalizeProcessingStatusForDisplay = (status) => {
  if (status === 'complete') return 'idle';
  return status || 'idle';
};

/**
 * Map from dashboard section ID to the Firestore documents.<key> used to look
 * up pdfStatus. Mirrors SECTION_DOCUMENT_KEY in DocumentsDashboard.js but
 * lives here so the helper stays pure and importable from tests.
 */
const SECTION_TO_DOC_KEY = {
  clinicalInterview:       'clinicalinterviewReport',
  semiStructuredInterview: 'semistructuredinterviewReport',
  diagnosticDecision:      'diagnosticdecisionReport',
  postAssessmentDiscussion:'postassessmentdiscussionReport',
  qbCheck:                 'qbcheckReport',
  qbTest:                  'qbtestReport',
  ratingScales:            'ratingScalesReport',
  coverPage:               'coverPageReport',
  patientReport:           'patientReport',
  bupaLetter:              'bupaletterReport',
};

/**
 * Build dashboard section-keyed processing statuses from a manageReports snapshot.
 * Used by DocumentsDashboard onSnapshot — pure so it can be unit-tested.
 *
 * When the optional ``documents`` map is supplied, the per-section status is
 * overlaid with the PDF availability: a section whose Firestore processing
 * status is "complete" (idle) but whose pdfStatus is "error" will be shown
 * as "error" so the row turns red and the Try again button appears. This
 * keeps the existing processing-status display pipeline intact while letting
 * the new PDF resilience data influence the row state.
 *
 * @param {Object|null|undefined} manageReports - dataModel.6_manageReports
 * @param {Object}               [documents={}] - patient.documents map
 * @returns {Object} Keys match DocumentsDashboard section IDs (qbCheck, patientReport, …)
 */
export const buildDashboardProcessingStatuses = (manageReports, documents = {}) => {
  const mr = manageReports || {};
  const docs = documents || {};
  const norm = normalizeProcessingStatusForDisplay;

  const base = {
    clinicalInterview: norm(mr.ciReportProcessingStatus),
    semiStructuredInterview: norm(mr.ssiReportProcessingStatus),
    diagnosticDecision: norm(mr.ddReportProcessingStatus),
    postAssessmentDiscussion: norm(mr.padReportProcessingStatus),
    qbCheck: norm(mr.qbCheckReportProcessingStatus),
    qbTest: norm(mr.qbTestReportProcessingStatus),
    ratingScales: norm(mr.ratingScalesReportProcessingStatus),
    coverPage: norm(mr.cpReportProcessingStatus),
    patientReport: norm(mr.prReportProcessingStatus),
    bupaLetter: norm(mr.bupaLetterReportProcessingStatus),
  };

  // Overlay PDF status when the processing status says "idle" (complete) but
  // the PDF itself is in an error or pending state. Without this, a row would
  // show green even though the PDF Preview/Download buttons are disabled.
  //
  // - pdfStatus 'error':   row turns red with Try Again button.
  // - pdfStatus 'pending': row turns amber with "Preparing PDF" label and
  //   Preview/Download disabled. The background retry sweep will eventually
  //   flip pdfStatus to 'ready' (success) or 'error' (exhaustion), and the
  //   onSnapshot listener picks up the change live.
  for (const [sectionId, docKey] of Object.entries(SECTION_TO_DOC_KEY)) {
    const entry = docs[docKey];
    if (!entry) continue;
    const pdfStatus = entry.pdfStatus;
    if (base[sectionId] === 'idle' && pdfStatus === 'error') {
      base[sectionId] = 'error';
    } else if (base[sectionId] === 'idle' && pdfStatus === 'pending') {
      base[sectionId] = 'pending';
    }
  }

  return base;
};

/**
 * Build a stable fingerprint string from the business fields in
 * manageReports.  Two snapshots with the same fingerprint have not
 * changed in any field that affects section row status.
 *
 * @param {Object|null|undefined} manageReports - The 6_manageReports object
 * @returns {string} Comma-separated values (empty string for missing fields)
 */
export const buildBusinessFieldsFingerprint = (manageReports) => {
  return FINGERPRINT_FIELDS.map((f) => manageReports?.[f] ?? '').join(',');
};

/**
 * Decide which processing-status keys should be updated in the UI
 * immediately vs delayed (debounced).
 *
 * When a key transitions OUT of 'processing', the UI keeps showing
 * 'processing' for a short window.  If the auto-helper retry sets
 * the key back to 'processing' within that window, the intermediate
 * completion state is never rendered and the user sees a smooth
 * amber -> amber transition instead of amber -> green -> amber.
 *
 * When a key transitions INTO 'processing', any pending debounce for
 * that key is cancelled and the update is applied immediately (the
 * user sees amber right away).
 *
 * @param {Object} prevStatuses - Previous processing status values (keyed by section ID)
 * @param {Object} newStatuses - Incoming processing status values from the latest snapshot
 * @param {Object} activeTimers - Mutable object of active setTimeout IDs, keyed by section ID.
 *   This function manages (creates/clears) timers as a side effect.
 * @param {(key: string, value: string) => void} onDebouncedUpdate - Callback invoked after
 *   the delay for each key whose debounced timer fires (not cancelled by a re-entry to 'processing').
 * @param {number} [delayMs=500] - How long to wait before applying the deferred status
 * @returns {Object} The statuses to apply immediately via setState
 */
export const computeDebouncedStatuses = (
  prevStatuses,
  newStatuses,
  activeTimers,
  onDebouncedUpdate,
  delayMs = 500
) => {
  const immediate = {};

  Object.keys(newStatuses).forEach((key) => {
    const prev = prevStatuses[key] || 'idle';
    const next = newStatuses[key];

    if (prev === 'processing' && next !== 'processing') {
      // Leaving processing — keep showing 'processing' and schedule deferred update
      immediate[key] = 'processing';

      if (activeTimers[key]) clearTimeout(activeTimers[key]);
      activeTimers[key] = setTimeout(() => {
        delete activeTimers[key];
        onDebouncedUpdate(key, next);
      }, delayMs);
    } else {
      if (next === 'processing' && activeTimers[key]) {
        // Re-entering processing — cancel any pending debounce so the
        // intermediate state is never shown
        clearTimeout(activeTimers[key]);
        delete activeTimers[key];
      }
      immediate[key] = next;
    }
  });

  return immediate;
};
